import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";
import { useTranslation } from "react-i18next";
import { ExternalLink, Summary, TrendingUpDown } from "lucide-react";
import {
  activityDashboard,
  activityDashboardReset,
  activityDashboardTotals,
  activityDashboardTotalsReset,
  listAdministrativeAreas,
  listAggregatorAdministrators,
  listAggregatorAdministratorsReset,
  listDeposits,
  listDepositsReset,
  listExchangeLocations,
  listExchangeLocationTypes,
  listMarketOffers,
  listMarketOffersReset,
  listMineAggregators,
  listTraderRepresentatives,
  listTraderRepresentativesReset,
  listTraders,
} from "../../actions";
import { displayErrorMessage, getCountryObjectFromCache } from "../../utils";
import { isUserGuest } from "../../utils/authentication";
import { type Deposit } from "../Deposits/DepositCard/deposit-dtos";
import { buildDepositParams, DEFAULT_DEPOSIT_FILTERS } from "../Deposits/utils";
import { type Offer } from "../RepresentativeOffers/components/OfferCard/interfaces";
import { refreshOfferThread } from "../RepresentativeOffers/components/OfferCommentsModal";
import {
  type AddressHolder,
  type CustomExchangeLocation,
  type DeliveryLookups,
  type NamedEntity,
} from "../RepresentativeOffers/types";
import { isExternalUser as getIsExternalUser, type UserData } from "@/utils/roles";
import { OverviewTiles, OverviewTilesSkeleton } from "./components/OverviewTiles";
import { WorkflowSummary, WorkflowSummarySkeleton } from "./components/WorkflowSummary";
import { MarketHighlights } from "./components/MarketHighlights";
import { type ActivityDashboardQueryParams, type OverviewData, type WorkflowData } from "./types";
import TimeRangeTabs from "@/components/TimeRangeTabs";
import { WFPButton } from "@/components/ui/button";
import { DEFAULT_TIME_RANGE_PERIOD, type TimeRangePeriod } from "@/constants/timeRange";

const COUNT_ONLY_PAGE_SIZE = 1;

const HIGHLIGHTS_PAGE_SIZE = 2;

/** `id` doubles as the `home.reportsHyperlinkCard.*` translation key suffix. */
const EXTERNAL_REPORTS = [
  {
    id: "tableau",
    href: "https://analytics.wfp.org/views/Farm2GoDashboard_17737548909260/HomePage",
  },
  {
    id: "dotsDashboard",
    href: "https://dots.wfp.org/workspace/module/view/latest/ri.workshop.main.module.152e6633-d6d3-413e-84f7-017f48715f5c",
  },
  {
    id: "agriculture",
    href: "https://engage.cloud.microsoft/main/org/wfp.org/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiI5OTIzNTY4NDM1MiJ9",
  },
] as const;

interface ResourceDetailState<TData> {
  data: TData;
  errorMessage: string;
  isFetching: boolean;
}

interface ListCountState {
  data: { count?: number };
  isFetching: boolean;
}

interface ListResultsState<TItem> {
  data: { results?: TItem[] };
  isFetching: boolean;
}

interface ResultsState<TItem> {
  data: { results?: TItem[] };
}

interface RootState {
  activityDashboard: ResourceDetailState<{ workflow?: WorkflowData }>;
  activityDashboardTotals: ResourceDetailState<{ overview?: Partial<OverviewData> }>;
  listAggregatorAdministrators: ListCountState;
  listTraderRepresentatives: ListCountState;
  listDeposits: ListResultsState<Deposit>;
  listMarketOffers: ListResultsState<Offer>;
  listTraders: ResultsState<AddressHolder>;
  listMineAggregators: ResultsState<AddressHolder>;
  listExchangeLocations: ResultsState<CustomExchangeLocation>;
  listExchangeLocationTypes: ResultsState<NamedEntity>;
  listAdministrativeAreas: ResultsState<NamedEntity>;
  userDetail: { data?: UserData };
}

interface StateProps {
  workflowData: WorkflowData | undefined;
  errorMessage: string;
  isFetching: boolean;
  totals: Partial<OverviewData>;
  isFetchingTotals: boolean;
  administratorsCount: number;
  isFetchingAdministrators: boolean;
  representativesCount: number;
  isFetchingRepresentatives: boolean;
  deposits: Deposit[];
  isFetchingDeposits: boolean;
  offers: Offer[];
  isFetchingOffers: boolean;
  deliveryLookups: DeliveryLookups;
  isExternalUser: boolean;
}

interface DispatchProps {
  getActivityDashboard: (period: TimeRangePeriod) => void;
  resetActivityDashboard: () => void;
  getActivityDashboardTotals: () => void;
  resetActivityDashboardTotals: () => void;
  getAdministrators: () => void;
  resetAdministrators: () => void;
  getRepresentatives: () => void;
  resetRepresentatives: () => void;
  getDeposits: () => void;
  resetDeposits: () => void;
  getOffers: () => void;
  resetOffers: () => void;
  getDeliveryLookups: () => void;
  getOfferThread: (offer: Offer) => void;
}

type WfpManagerHomeProps = StateProps & DispatchProps;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

export const WfpManagerHome: React.FC<WfpManagerHomeProps> = ({
  workflowData,
  isFetching,
  errorMessage,
  totals,
  isFetchingTotals,
  administratorsCount,
  isFetchingAdministrators,
  representativesCount,
  isFetchingRepresentatives,
  deposits,
  isFetchingDeposits,
  offers,
  isFetchingOffers,
  deliveryLookups,
  isExternalUser,
  getActivityDashboard,
  resetActivityDashboard,
  getActivityDashboardTotals,
  resetActivityDashboardTotals,
  getAdministrators,
  resetAdministrators,
  getRepresentatives,
  resetRepresentatives,
  getDeposits,
  resetDeposits,
  getOffers,
  resetOffers,
  getDeliveryLookups,
  getOfferThread,
}) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<TimeRangePeriod>(DEFAULT_TIME_RANGE_PERIOD);
  const countryName = getCountryObjectFromCache()?.name ?? "";
  const isGuest = isUserGuest(localStorage.getItem("user_profile"));

  const hasRequestedLookups = useRef(false);

  const openOffer = useCallback(
    (offer: Offer) => {
      if (!hasRequestedLookups.current) {
        hasRequestedLookups.current = true;
        getDeliveryLookups();
      }
      getOfferThread(offer);
    },
    [getDeliveryLookups, getOfferThread]
  );

  const isOverviewLoading =
    isFetchingTotals || isFetchingAdministrators || isFetchingRepresentatives;

  useEffect(() => {
    getActivityDashboard(period);
  }, [getActivityDashboard, period]);

  useEffect(() => {
    getActivityDashboardTotals();
    getAdministrators();
    getRepresentatives();
    if (!isGuest) {
      getDeposits();
      getOffers();
    }
    return () => {
      resetActivityDashboard();
      resetActivityDashboardTotals();
      resetAdministrators();
      resetRepresentatives();
      resetDeposits();
      resetOffers();
    };
  }, [
    isGuest,
    getActivityDashboardTotals,
    getAdministrators,
    getRepresentatives,
    resetActivityDashboard,
    resetActivityDashboardTotals,
    resetAdministrators,
    resetRepresentatives,
    getDeposits,
    resetDeposits,
    getOffers,
    resetOffers,
  ]);

  return (
    <main className="flex flex-col bg-muted gap-8 md:gap-12 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <h1 className="flex items-center gap-2 font-semibold text-2xl">
        <Summary className="size-7 shrink-0 text-blue-600" aria-hidden="true" />
        {t("home.manager.overview.title", { country: countryName })}
      </h1>

      {!isGuest && (
        <MarketHighlights
          deposits={deposits}
          isFetchingDeposits={isFetchingDeposits}
          offers={offers}
          isFetchingOffers={isFetchingOffers}
          deliveryLookups={deliveryLookups}
          onOpenOffer={openOffer}
        />
      )}

      {isOverviewLoading ? (
        <OverviewTilesSkeleton />
      ) : (
        <OverviewTiles
          totals={totals}
          administratorsCount={administratorsCount}
          representativesCount={representativesCount}
          isExternalUser={isExternalUser}
        />
      )}

      <section className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 flex-col gap-1">
            <h2 className="flex items-center gap-2 font-semibold text-xl">
              <TrendingUpDown className="size-7 shrink-0 text-blue-600" aria-hidden="true" />
              {t("home.manager.overview.workflow.title")}
            </h2>
            <p className="text-muted-foreground">{t("home.manager.overview.workflow.subtitle")}</p>
          </div>
          <TimeRangeTabs
            value={period}
            onValueChange={setPeriod}
            ariaLabel={t("wfpManagerHome.timeRange.ariaLabel")}
            className="w-full shrink-0 items-center md:w-auto md:items-end"
          />
        </div>
        {isFetching ? (
          <WorkflowSummarySkeleton />
        ) : workflowData ? (
          <WorkflowSummary data={workflowData} />
        ) : (
          displayErrorMessage(errorMessage)
        )}
      </section>

      {!isExternalUser && (
        <div className="flex flex-wrap justify-center gap-3">
          {EXTERNAL_REPORTS.map(({ id, href }) => (
            <WFPButton key={id} variant="outline" asChild>
              <a href={href} target="_blank" rel="noopener noreferrer">
                <span>{t(`home.reportsHyperlinkCard.${id}`)}</span>
                <ExternalLink aria-hidden="true" className="size-4" />
              </a>
            </WFPButton>
          ))}
        </div>
      )}
    </main>
  );
};

export const mapStateToProps = (state: RootState): StateProps => ({
  workflowData: state.activityDashboard.data.workflow,
  isFetching: state.activityDashboard.isFetching,
  errorMessage: state.activityDashboard.errorMessage,
  totals: state.activityDashboardTotals.data.overview ?? {},
  isFetchingTotals: state.activityDashboardTotals.isFetching,
  administratorsCount: state.listAggregatorAdministrators.data.count ?? 0,
  isFetchingAdministrators: state.listAggregatorAdministrators.isFetching,
  representativesCount: state.listTraderRepresentatives.data.count ?? 0,
  isFetchingRepresentatives: state.listTraderRepresentatives.isFetching,
  deposits: state.listDeposits.data.results ?? [],
  isFetchingDeposits: state.listDeposits.isFetching,
  offers: state.listMarketOffers.data.results ?? [],
  isFetchingOffers: state.listMarketOffers.isFetching,
  deliveryLookups: {
    traders: state.listTraders.data.results ?? [],
    aggregators: state.listMineAggregators.data.results ?? [],
    customExchangeLocations: state.listExchangeLocations.data.results ?? [],
    exchangeLocationTypes: state.listExchangeLocationTypes.data.results ?? [],
    administrativeAreas: state.listAdministrativeAreas.data.results ?? [],
  },
  isExternalUser: getIsExternalUser(state.userDetail?.data ?? { user_groups: [] }),
});

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  getActivityDashboard: (period: TimeRangePeriod) => {
    const params: ActivityDashboardQueryParams = { period, include: "workflow" };
    dispatchAction(dispatch, activityDashboard("", "", false, params));
  },
  resetActivityDashboard: () => dispatchAction(dispatch, activityDashboardReset()),
  // The tiles are cumulative by design, so the overview block carries no period.
  getActivityDashboardTotals: () => {
    const params: ActivityDashboardQueryParams = { include: "overview" };
    dispatchAction(dispatch, activityDashboardTotals("", "", false, params));
  },
  resetActivityDashboardTotals: () => dispatchAction(dispatch, activityDashboardTotalsReset()),
  // `active: true` excludes archived administrators: the list endpoint serves them
  // by default so the aggregator detail page can offer to re-enable them.
  getAdministrators: () =>
    dispatchAction(
      dispatch,
      listAggregatorAdministrators({ page_size: COUNT_ONLY_PAGE_SIZE, active: true })
    ),
  resetAdministrators: () => dispatchAction(dispatch, listAggregatorAdministratorsReset()),
  getRepresentatives: () =>
    dispatchAction(dispatch, listTraderRepresentatives({ page_size: COUNT_ONLY_PAGE_SIZE })),
  resetRepresentatives: () => dispatchAction(dispatch, listTraderRepresentativesReset()),
  getDeposits: () =>
    dispatchAction(
      dispatch,
      listDeposits({
        ...buildDepositParams(DEFAULT_DEPOSIT_FILTERS, 1),
        page_size: HIGHLIGHTS_PAGE_SIZE,
      })
    ),
  resetDeposits: () => dispatchAction(dispatch, listDepositsReset()),
  getOffers: () =>
    dispatchAction(
      dispatch,
      listMarketOffers({
        page: 1,
        page_size: HIGHLIGHTS_PAGE_SIZE,
        ordering: "-publishing_datetime",
        with_exchange: true,
      })
    ),
  resetOffers: () => dispatchAction(dispatch, listMarketOffersReset()),
  getDeliveryLookups: () => {
    dispatchAction(dispatch, listTraders());
    dispatchAction(dispatch, listMineAggregators());
    dispatchAction(dispatch, listExchangeLocations());
    dispatchAction(dispatch, listExchangeLocationTypes());
    dispatchAction(
      dispatch,
      listAdministrativeAreas({ page_size: 9999, page: 1, country: getCountryObjectFromCache().id })
    );
  },
  getOfferThread: (offer: Offer) => refreshOfferThread(dispatch, offer),
});

export const WfpManagerHomeConnected = connect(mapStateToProps, mapDispatchToProps)(WfpManagerHome);
